import type { ApolloClient } from '@apollo/client';
import {
    ExternalApiTarget,
    NyDepRegistrationErrorCode,
    NyDepRegistrationStatus,
    GetNyDepIntegrationDocument,
    RegisterNyDepDeviceDocument,
    SwarmType,
} from '@/graphql/types';
import type {
    GetNyDepIntegrationQuery,
    GetNyDepIntegrationQueryVariables,
    NyDepIntegration,
    RegisterNyDepDeviceMutation,
    RegisterNyDepDeviceMutationVariables,
} from '@/graphql/types';
import type { Observable } from 'rxjs';
import {
    catchError,
    concatMap,
    filter,
    from,
    map,
    of,
    shareReplay,
    Subscription,
    switchMap,
    take,
    tap,
} from 'rxjs';
import type { SwarmFormController } from './form-controller';

interface NyDepRegistrationDetails {
    externalApiTarget: ExternalApiTarget | null | undefined;
    location: string | null | undefined;
    deviceName: string | null | undefined;
}

interface NyDepFormValues {
    externalApiTarget?: ExternalApiTarget | null;
    location?: string | null;
    deviceName?: string | null;
}

interface NyDepDeviceRegistrationServiceConfig {
    client: ApolloClient<object>;
    formController: SwarmFormController;
    reportError: (message: string) => void;
}

type NyDepRegistrationResponse = NonNullable<RegisterNyDepDeviceMutation['registerNyDepDevice']>;
type NyDepIntegrationState = Pick<NyDepIntegration, 'registrationStatus' | 'registrationErrorCode'>;

function getRegistrationDetails(formController: SwarmFormController): NyDepRegistrationDetails {
    const { externalApiTarget, location, deviceName } =
        formController.rhfProps.getValues() as NyDepFormValues;

    return {
        externalApiTarget,
        location,
        deviceName,
    };
}

function registrationDetailsChanged(
    previous: NyDepRegistrationDetails | null,
    current: NyDepRegistrationDetails
): boolean {
    return (
        previous === null ||
        previous.externalApiTarget !== current.externalApiTarget ||
        previous.location !== current.location ||
        previous.deviceName !== current.deviceName
    );
}

function isNyDepMeasuringPoint(
    formController: SwarmFormController,
    registrationDetails: NyDepRegistrationDetails
): boolean {
    return (
        !formController.isProject &&
        formController.swarmType === SwarmType.Sound &&
        registrationDetails.externalApiTarget === ExternalApiTarget.NewYorkApi
    );
}

function shouldUpdateRegistrationBaseline(
    registration: NyDepRegistrationResponse | null | undefined
): boolean {
    // Keep the baseline unchanged for failures a later save can resolve without
    // changing registration details, so that save triggers another registration attempt.
    return (
        registration != null &&
        !(
            registration.status === NyDepRegistrationStatus.Failed &&
            (registration.errorCode === NyDepRegistrationErrorCode.ExternalApiError ||
                registration.errorCode === NyDepRegistrationErrorCode.InvalidConfiguration)
        )
    );
}

function shouldRetryUnchangedIntegration(integration: NyDepIntegrationState | null): boolean {
    return (
        integration?.registrationStatus === NyDepRegistrationStatus.Pending ||
        (integration?.registrationStatus === NyDepRegistrationStatus.Failed &&
            (integration.registrationErrorCode === NyDepRegistrationErrorCode.ExternalApiError ||
                integration.registrationErrorCode ===
                    NyDepRegistrationErrorCode.InvalidConfiguration))
    );
}

function getRegistrationErrorMessage(errorCode: NyDepRegistrationErrorCode | null | undefined) {
    if (!errorCode) {
        throw new Error('FAILED registration status received without an error code');
    }

    const messages: Record<NyDepRegistrationErrorCode, string> = {
        [NyDepRegistrationErrorCode.InvalidConfiguration]: gettext(
            'NY_DEP_REGISTRATION_ERROR_INVALID_CONFIGURATION'
        ),
        [NyDepRegistrationErrorCode.AddressCoordinateMismatch]: gettext(
            'NY_DEP_REGISTRATION_ERROR_ADDRESS_COORDINATE_MISMATCH'
        ),
        [NyDepRegistrationErrorCode.InvalidRegistrationDetails]: gettext(
            'NY_DEP_REGISTRATION_ERROR_INVALID_DETAILS'
        ),
        [NyDepRegistrationErrorCode.ExternalApiError]: gettext('NY_DEP_REGISTRATION_ERROR_GENERIC'),
    };

    return messages[errorCode] ?? gettext('NY_DEP_REGISTRATION_ERROR_GENERIC');
}

/**
 * Registers NY DEP devices after a successful local measuring-point save.
 *
 * The service is independent from React and listens to the form controller's
 * lifecycle streams. Registration requests are serialized so a newer save
 * cannot race an earlier registration response.
 */
export class NyDepDeviceRegistrationService {
    private readonly client: ApolloClient<object>;

    private readonly formController: SwarmFormController;

    private readonly reportError: (message: string) => void;

    private lastSavedRegistrationDetails: NyDepRegistrationDetails | null = null;

    private integration: NyDepIntegrationState | null = null;

    private readonly initialIntegrationState$: Observable<NyDepIntegrationState | null>;

    constructor({ client, formController, reportError }: NyDepDeviceRegistrationServiceConfig) {
        this.client = client;
        this.formController = formController;
        this.reportError = reportError;
        this.initialIntegrationState$ = this.createInitialIntegrationState$();
    }

    connect(): Subscription {
        const subscription = new Subscription();

        subscription.add(this.initialIntegrationState$.subscribe());

        subscription.add(
            this.formController.onSubmitSuccessful$
                .pipe(
                    // Provider calls can outlive the local save. Process them one
                    // at a time so a later re-registration cannot be overwritten
                    // by an earlier response.
                    concatMap(({ id }) =>
                        this.initialIntegrationState$.pipe(
                            take(1),
                            concatMap(() => from(this.registerDevice(id)))
                        )
                    )
                )
                .subscribe()
        );

        return subscription;
    }

    private createInitialIntegrationState$(): Observable<NyDepIntegrationState | null> {
        const measuringPointId = this.formController.id;
        if (!measuringPointId) {
            return of(null);
        }

        return this.formController.serverValuesLoaded$.pipe(
            filter(Boolean),
            take(1),
            map(() => getRegistrationDetails(this.formController)),
            tap((registrationDetails) => this.setRegistrationBaseline(registrationDetails)),
            switchMap((registrationDetails) => {
                if (!isNyDepMeasuringPoint(this.formController, registrationDetails)) {
                    return of(null);
                }

                return from(
                    this.client.query<GetNyDepIntegrationQuery, GetNyDepIntegrationQueryVariables>({
                        query: GetNyDepIntegrationDocument,
                        variables: { measuringPointId },
                        fetchPolicy: 'network-only',
                    })
                ).pipe(
                    map(
                        (response): NyDepIntegrationState =>
                            response.data.nyDepIntegration ?? {
                                // A saved NY DEP MP without a state row still needs
                                // registration. The save will persist PENDING; treat
                                // it the same way now so this user action can register.
                                registrationStatus: NyDepRegistrationStatus.Pending,
                                registrationErrorCode: null,
                            }
                    ),
                    // Registration remains driven by changed details when the
                    // optional state lookup is unavailable.
                    catchError(() => of(null))
                );
            }),
            tap((integration) => {
                this.integration = integration;
            }),
            shareReplay({ bufferSize: 1, refCount: true })
        );
    }

    private setRegistrationBaseline(registrationDetails: NyDepRegistrationDetails): void {
        this.lastSavedRegistrationDetails = registrationDetails;
    }

    private async registerDevice(id: string): Promise<void> {
        const currentRegistrationDetails = getRegistrationDetails(this.formController);

        if (!isNyDepMeasuringPoint(this.formController, currentRegistrationDetails)) {
            // Keep the latest local state as the baseline when NY DEP is disabled,
            // so enabling it on a future save is detected as a relevant change.
            this.setRegistrationBaseline(currentRegistrationDetails);
            return;
        }

        if (!currentRegistrationDetails.deviceName) {
            // A previously registered device can still drain delayed measurement
            // data after detachment. Do not replace that registration until a
            // device is attached again.
            this.setRegistrationBaseline(currentRegistrationDetails);
            return;
        }

        if (
            !shouldRetryUnchangedIntegration(this.integration) &&
            !registrationDetailsChanged(
                this.lastSavedRegistrationDetails,
                currentRegistrationDetails
            )
        ) {
            return;
        }

        try {
            const response = await this.client.mutate<
                RegisterNyDepDeviceMutation,
                RegisterNyDepDeviceMutationVariables
            >({
                mutation: RegisterNyDepDeviceDocument,
                variables: { id },
            });
            const registration = response.data?.registerNyDepDevice;
            if (registration) {
                this.integration = {
                    registrationStatus: registration.status,
                    registrationErrorCode: registration.errorCode,
                };
            }

            if (shouldUpdateRegistrationBaseline(registration)) {
                this.setRegistrationBaseline(currentRegistrationDetails);
            }

            if (registration?.status === NyDepRegistrationStatus.Failed) {
                this.reportError(getRegistrationErrorMessage(registration.errorCode));
            } else if (registration?.status === NyDepRegistrationStatus.NotReady) {
                this.reportError(gettext('NY_DEP_REGISTRATION_ERROR_NOT_READY'));
            }
        } catch {
            this.reportError(gettext('NY_DEP_REGISTRATION_ERROR_GENERIC'));
        }
    }
}
